import React, { useEffect, useRef, useState, useCallback } from "react";
import { useStore } from "../../store/store.context";
import {
  UPDATE_CASE_DETAILS_API,
  SEND_EMAIL_API,
  CHAT_API,
  API_URL,
} from "../../services/api-config";
import {
  CHAT_CONSTANTS,
  MESSAGE_ACTION_TYPES,
} from "../../constants/constants";
import ChatHeader from "./chat-header/chat-header";
import ChatInput from "./chat-input/chat-input";
import useChatMessages from "../../hooks/use-chat-messages";
import useWebSocket from "../../hooks/use-web-socket";
import Message from "./message/message";

import "./message-list.scss";

const MessageList = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [lastButtonMsgIndex, setLastButtonMsgIndex] = useState(null); 

  const chatContainerRef = useRef(null);
  const messagesEndRef = useRef(null);
  const lastSendMessage = useRef(null);

  const { updateStore, store } = useStore();
  const { messages, addMessage, setMessages, dispatch } = useChatMessages();
    const updatedUserInfo = sessionStorage.getItem('userInfo');
  const parsedUserInfo = updatedUserInfo ? JSON.parse(updatedUserInfo) : {};

  const clearButtons = (index = lastButtonMsgIndex) => {
    if (index === null || index === undefined) return;
    dispatch({
      type: MESSAGE_ACTION_TYPES.UPDATE_MESSAGE,
      index,
      payload: { buttons: [] },
    });
    setLastButtonMsgIndex(null);
  };

  const addUserMessage = (text) => {
    addMessage({
      text,
      isUser: true,
      timestamp: new Date().toISOString(),
    });
  };

  const addSystemMessage = (text) => {
    addMessage({
      text,
      isUser: false,
      timestamp: new Date().toISOString(),
    });
  };

  useEffect(() => {
    updateStore({ caseId: "", initialCaseSelect: false });
    setMessages([
      {
        text: (
          <>
            {CHAT_CONSTANTS.WELCOME_MESSAGE}
            <br />
          </>
        ),
        isUser: false,
        timestamp: new Date().toISOString(),
      },
    ]);
  }, []);

  useEffect(() => {
    if (store?.promptQuery) {
      // Bypass LLM for "show me the case details" queries — use direct REST fetch
      const caseMatch = store.promptQuery.match(/show me the case details for\s+(.+)/i);
      if (caseMatch) {
        const caseId = caseMatch[1].trim();
        fetchCaseDetailsDirect(caseId);
      } else {
        handleExampleClick(store?.promptQuery);
      }
    }
  }, [store?.promptQuery, store?.promptQueryNonce]);

  // useEffect(() => {
  //   if (
  //     store.caseId &&
  //     store?.detailsStatus &&
  //     (store?.detailsStatus === "NON-COMPLIANT" ||
  //       store?.detailsStatus === "COMPLIANT" ||
  //       store?.detailsStatus === "EXCEPTION")
  //   ) {
  //     setTimeout(() => {
  //       const message = {
  //         text: `You have opened case ID: ${store.caseId}. How can I assist you with this case?`,
  //         isUser: false,
  //         timestamp: new Date().toISOString(),
  //         buttons: [
  //           {
  //             label: "Reject",
  //             onClick: (index) => handleButtonClick("reject", index),
  //             color: " #B80012",
  //           },
  //           {
  //             label: "Approve",
  //             onClick: (index) => handleButtonClick("accept", index),
  //             color: " #40AB35",
  //           },
  //         ],
  //       };
  //       addMessage(message);
  //       setLastButtonMsgIndex(messages.length);
  //     }, 1000);
  //   }
  // }, [store?.detailsStatus]);

  useEffect(() => {
    if (store.previousSendMessage) {
      handleExampleClick(lastSendMessage.current);
    }
  }, [store?.previousSendMessage]);

  const sendEmail = async (messageIndex) => {
    try {
      const payload = {
        casenumber: store?.caseId,
        generated_email:
          store?.status?.mailContent ||
          "Hello this is alternate email content ",
        recipient: store?.status["to:"] || "aniruddhya.dutta1@ibm.com",
      };

      const response = await fetch(SEND_EMAIL_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        console.error("Failed to send email:", response.status);
        addSystemMessage(`Failed to send email.  Status: ${response.status}`);
        return;
      }

      const data = await response.json();
      addSystemMessage(data.status);
    } catch (error) {
      addSystemMessage(`Error sending email: ${error.message}`);
    }

    if (store.caseId) {
      const newStatus = store.status?.casestatus === 104 ? "Approved" : "Rejected";
      refreshCaseDetailsLocally(store.caseId, newStatus);
    }

    clearButtons(messageIndex);
  };

  useEffect(() => {
    if (store?.caseId && store?.status?.casestatus) {
      const Status = store.status.casestatus === 104 ? "approv" : "reject";
      const message = {
        text: `You have ${Status}ed the AFR request. Would you also like to send an email?
          Here’s a pre-generated content. This email will be received by ${store?.status["to:"]}. Click on send button to forward the email.`,
        isUser: false,
        timestamp: new Date().toISOString(),
        buttons: [
          {
            label: "Cancel",
            color: " #42B0D5",
            onClick: (index) => handleCancelClick("cancel", index),
          },
          {
            label: "Send",
            color: " #00243D",
            onClick: (index) => sendEmail(index),
          },
        ],
      };
      addMessage(message);
      setLastButtonMsgIndex(messages.length);
    }
  }, [store?.status?.casestatus]);

  useEffect(() => {
    setTimeout(() => {
      if (chatContainerRef.current) {
        chatContainerRef.current.scrollTop =
          chatContainerRef.current.scrollHeight;
      }
    }, 0);

    if (messages.length && !messages[messages.length - 1].isUser) {
      setIsTyping(false);
    }

    updateStore({ dashboardStatus: "" });
  }, [messages]);

  const handleMessage = useCallback(
    (event) => {
      try {
        const response = JSON.parse(event.data);
        const responseBody = response?.body?.response;
        const responseData = responseBody?.data;
        const isError = responseBody?.success === false;

        if (isError) {
          // Error response (e.g. timeout, tool failure) — clear loading and show friendly message
          updateStore({ loading: false });
          const errorText = (typeof responseData === "string" && responseData)
            || "Sorry, something went wrong while processing your request. Please try again.";
          addSystemMessage(errorText);
          lastSendMessage.current = null;
        } else if (typeof responseData === "string") {
          // This is an intermediate message (like "Calling the database service")
          // Don't clear loading state yet - keep showing loader in content area
          addSystemMessage(responseData);
          lastSendMessage.current = responseData;
        } else {
          // This is the actual data response - now we can clear loading
          updateStore({ loading: false });
          
          addSystemMessage(response.body.response.message);
          lastSendMessage.current = response.body.response;

          updateStore({ apiResponseData: response.body.response });
        }
      } catch (err) {
        console.error("Failed to parse WebSocket message:", err);
        // Clear loading state on error as well
        updateStore({ loading: false });
      }
    },
    [addMessage, updateStore]
  );

  const { sendMessage } = useWebSocket(`${CHAT_API}`, handleMessage);

  const handleExampleClick = (query) => {
    // Remove previous buttons if user sends a new query
    clearButtons();

    setIsTyping(true);
    addUserMessage(query);
    sendMessageToSocket(query);
  };

  const onSendQuestion = () => {
    if (!searchQuery.trim()) return;

    // Remove previous buttons
    clearButtons();

    updateStore({ detailsStatus: "" });

    // Bypass LLM for case details queries — use direct REST fetch
    const caseMatch = searchQuery.match(/show me the case details for\s+(.+)/i)
      || searchQuery.match(/(\d+-\d+)/);
    if (caseMatch) {
      const caseId = caseMatch[1].trim();
      setSearchQuery("");
      fetchCaseDetailsDirect(caseId);
      return;
    }

    setIsTyping(true);
    addUserMessage(searchQuery);
    sendMessageToSocket(searchQuery);
    setSearchQuery("");
  };

  const applyCaseDetailsResult = (body, query) => {
    if (body && body.data && body.data.length > 0) {
      const firstRow = Array.isArray(body.data) ? body.data[0] : null;
      const isRecentStatusUpdate =
        String(store.caseId || "") === String(firstRow?.casenumber || "")
        && !!store.detailsStatus
        && !!store.lastModifiedBy;
      const resolvedMessage = isRecentStatusUpdate
        ? (buildUpdatedCaseSummaryMessage(firstRow, firstRow?.casenumber) || body.message || query)
        : (body.message || query);

      updateStore({
        loading: false,
        apiResponseData: {
          type: body.type || "Details",
          data: body.data,
          message: resolvedMessage,
        },
      });
      addSystemMessage(resolvedMessage);
      return;
    }

    updateStore({ loading: false });
    addSystemMessage("No case details found.");
  };

  useEffect(() => {
    if (store.caseId && store.initialCaseSelect) {
      // Reset flag immediately to prevent re-trigger
      // Also clear dashboardStatus and isCaseSummaryView to prevent count APIs from firing
      updateStore({
        initialCaseSelect: false,
        dashboardStatus: "",
        isCaseSummaryView: false,
      });

      fetchCaseDetailsDirect(store.caseId);
    }
  }, [store.caseId, store.initialCaseSelect]);

  const formatDisplayDate = (value) => {
    const dt = value ? new Date(value) : new Date();
    if (Number.isNaN(dt.getTime())) return new Date().toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
    return dt.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  const buildUpdatedCaseSummaryMessage = (row, fallbackCaseId) => {
    if (!row) return null;
    const caseNumber = row.casenumber || fallbackCaseId;
    const rawStatus = (row.casestatus || store.detailsStatus || "updated").toString();
    const statusUpper = rawStatus.toUpperCase();
    const statusText = rawStatus.toLowerCase();
    const reason = (row.casestatusreason || "").toString().trim();
    const country = row.country || "the assigned country";
    const modifiedOn = formatDisplayDate(
      row.modifieddate || row.modifieddatetime || row.lastmodifieddate || new Date().toISOString()
    );

    const isApprovedLike = statusUpper === "APPROVED";

    if (isApprovedLike) {
      return `The Case Number ${caseNumber} has been ${statusText}, was closed in ${country}, and was last modified on ${modifiedOn}; for a detailed breakdown, please refer to the information on the right side of the screen.`;
    }

    return `The Case Number ${caseNumber} has a status of ${statusUpper} due to ${reason || "an update"} and is assigned to a case manager in ${country}, with its last modified date on ${modifiedOn}. For more details, you can find a detailed breakdown on the right side of the screen.`;
  };

  // Direct REST fetch for case details — bypasses LLM for faster response
  const fetchCaseDetailsDirect = async (caseId) => {
    const query = `Show me the case details for ${caseId}`;
    addUserMessage(query);
    setIsTyping(true);
    updateStore({ loading: true });
    try {
      const resp = await fetch(
        `${API_URL}/getcasedetails-direct/${encodeURIComponent(caseId)}`
      );
      const json = await resp.json();
      const body = json?.body;
      applyCaseDetailsResult(body, query);
    } catch (err) {
      updateStore({ loading: false });
      addSystemMessage("Failed to load case details. Please try again.");
    } finally {
      setIsTyping(false);
    }
  };

  // Refresh case details using already-loaded data (no network call) when only status changed
  const refreshCaseDetailsLocally = (caseId, newStatus) => {
    const existingData = store.apiResponseData;
    if (existingData && existingData.type === "Details" && Array.isArray(existingData.data)) {
      const patchedData = existingData.data.map((row) => {
        if (String(row.casenumber) === String(caseId)) {
          return { ...row, casestatus: newStatus };
        }
        return row;
      });
      const statusLabel = newStatus || "updated";
      const message = `Case ${caseId} status has been updated to "${statusLabel}". Details are shown on the right.`;
      updateStore({
        apiResponseData: {
          ...existingData,
          data: patchedData,
          message,
        },
      });
      addSystemMessage(message);
    } else {
      // Fallback to REST fetch if we don't have cached data
      fetchCaseDetailsDirect(caseId);
    }
  };

  const handleCancelClick = async (buttonName, index) => {
    if (buttonName === "cancel" && store.caseId) {
      const newStatus = store.status?.casestatus === 104 ? "Approved" : "Rejected";
      refreshCaseDetailsLocally(store.caseId, newStatus);
    }

    clearButtons(index);
  };

  const updateCaseDetailsStatus = async (casestatusid, casestatusreason = "") => {
    const response = await fetch(`${UPDATE_CASE_DETAILS_API}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        casenumber: store?.caseId,
        casestatusid,
        casestatusreason,
        modifiedBy: parsedUserInfo?.email,
      }),
    });

    return response.json();
  };

  useEffect(() => {
    if (store?.remarksReason) {
      const updateCaseDetails = async () => {
        try {
          const data = await updateCaseDetailsStatus(105, store?.remarksReason || "");

          updateStore({
            status: {
              ...data,
              casestatus: 105,
              mail: true,
              mailContent: data?.response,
            },
          });
        } catch (error) {
          console.error("Error updating case details:", error);
        }
      };
      updateCaseDetails();
    }
  }, [store?.remarksReason]);

  const handleButtonClick = async (buttonName, index) => {
    if (buttonName === "reject") {
      addSystemMessage(`You have selected to Reject the AFR request. Add remark for the action on AFR request ${store.caseId}.`);

      addMessage({
        isUser: true,
        timestamp: new Date().toISOString(),
        inputs: [],
      });

      clearButtons(index);
    } else if (buttonName === "accept") {
      const data = await updateCaseDetailsStatus(104, "");

      updateStore({
        status: {
          ...data,
          casestatus: 104,
          mail: true,
          mailContent: data?.response,
        },
      });

      clearButtons(index);
    }

    setLastButtonMsgIndex(null);
  };

  const sendMessageToSocket = (query) => {
    let userEmail = null;
    try {
      const rawUserInfo = sessionStorage.getItem('userInfo');
      if (rawUserInfo) {
        const parsed = JSON.parse(rawUserInfo);
        userEmail = parsed?.email || parsed?.userEmail || null;
      }
    } catch (_) {}
    const payload = { question: query };
    if (userEmail) payload.user_email = userEmail;
    const message = JSON.stringify(payload);
    sendMessage(message);
    // NOTE: do NOT call setIsTyping(false) here — the typing indicator must
    // stay on until the server response arrives (handled by the messages useEffect).
  };

  const disableSendBtn = isTyping || searchQuery.trim() === "";

  return (
    <div className="message-list-container" ref={messagesEndRef}>
      <ChatHeader />
      <Message
        messages={messages}
        chatContainerRef={chatContainerRef}
        isTyping={isTyping}
      />
      <ChatInput
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        onSend={onSendQuestion}
        disableSendBtn={disableSendBtn}
      />
    </div>
  );
};

export default MessageList;
